import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Collapse, Grid, MenuItem, Stack } from '@mui/material'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { makeZodI18nMap } from 'zod-i18n-map'
import { useHasEnablement } from '@portal/pkg-authorization'
import { HookFormTextField, HookFormSelect, HookFormCheckbox } from '@portal/pkg-hook-form'
import { InfoLabel, SideDrawer } from '@portal/pkg-ui'
import { useEditUser } from '../mutations'
import {
  EditUserSchema,
  EditUserFormValues,
  LicenseType,
  IntegrationRoleOption,
  NotificationFrequencyOption,
} from '../model/editUserSchema'
import { NotificationFrequency } from '@portal/pkg-api/gen/admin-console'
import type { UserDetailResponse } from '@portal/pkg-api/gen/admin-console'
import { UserQueries } from '../queries'
import { isLicenseLimitExceeded, isOrganizationBusy } from '../problems'

const COGNITO_AUTH_SOURCE = 'COGNITO'

interface EditUserFormProps {
  user: UserDetailResponse
  onClose: () => void
  orgId?: number
}

function toFormValues(user: UserDetailResponse): EditUserFormValues {
  return {
    firstName: user.givenName,
    lastName: user.familyName,
    email: user.email,
    licenseType: user.licenseType || LicenseType.LITE,
    integrationsRole: user.integrationsRole || IntegrationRoleOption.NOT_SELECTED,
    projectParticipant: user.projectParticipant,
    emailNotifications:
      user.emailNotifications && user.emailNotificationsFrequency !== NotificationFrequency.NONE,
    notificationFrequency:
      user.emailNotificationsFrequency === NotificationFrequency.NONE
        ? NotificationFrequencyOption.WEEKLY
        : user.emailNotificationsFrequency || NotificationFrequencyOption.WEEKLY,
  }
}

export function EditUserForm({ user, onClose, orgId }: EditUserFormProps) {
  const { t } = useTranslation('admin-console')
  const hasAdminEnablement = useHasEnablement('Admin', 'ADMIN', { global: true })
  const { data: userAdminIntegrationStatus } = useSuspenseQuery(UserQueries.orgSettings(orgId))

  const { enqueueSnackbar } = useSnackbar()
  const editUser = useEditUser(orgId)
  const isManagedUser = user.authSource === COGNITO_AUTH_SOURCE
  const hasExistingIntegrationRole = !!user.integrationsRole
  const showIntegrationsRole = hasAdminEnablement && userAdminIntegrationStatus.integrationsEnabled

  const form = useForm<EditUserFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(EditUserSchema, {
      errorMap: makeZodI18nMap({ handlePath: false }),
    }),
    defaultValues: toFormValues(user),
  })

  const {
    handleSubmit,
    control,
    formState: { isValid, isDirty },
  } = form

  const emailNotifications = useWatch({ control, name: 'emailNotifications' })

  const onSubmit = (formData: EditUserFormValues) => {
    editUser.mutate(
      { id: user.id, ...formData },
      {
        onSuccess: () => {
          enqueueSnackbar(t('users.table.userUpdated'), { variant: 'success' })
          onClose()
        },
        onError: (error: unknown) => {
          const messageKey = isLicenseLimitExceeded(error)
            ? 'users.form.licenseLimitExceeded'
            : isOrganizationBusy(error)
              ? 'users.form.organizationBusy'
              : 'users.form.saveFailed'
          enqueueSnackbar(t(messageKey), { variant: 'error' })
        },
      },
    )
  }

  return (
    <SideDrawer
      title={t('users.drawer.editTitle')}
      onClose={onClose}
      actions={
        <Stack direction="row" spacing={1}>
          <Button color="secondary" onClick={onClose}>
            {t('users.drawer.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!isDirty || !isValid || editUser.isPending}
            onClick={() => void handleSubmit(onSubmit)()}
          >
            {t('users.drawer.save')}
          </Button>
        </Stack>
      }
    >
      <FormProvider {...form}>
        <Box component="form" noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <HookFormTextField<EditUserFormValues>
                name="firstName"
                label={t('users.form.firstName')}
                variant="outlined"
                required
                fullWidth
                size="small"
                disabled={!isManagedUser}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <HookFormTextField<EditUserFormValues>
                name="lastName"
                label={t('users.form.lastName')}
                variant="outlined"
                required
                fullWidth
                size="small"
                disabled={!isManagedUser}
              />
            </Grid>

            <Grid size={{ xs: 6 }}>
              <HookFormTextField<EditUserFormValues>
                name="email"
                label={t('users.form.email')}
                variant="outlined"
                fullWidth
                size="small"
                disabled
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <HookFormSelect<EditUserFormValues>
                name="licenseType"
                label={
                  <InfoLabel
                    label={t('users.form.licenseType')}
                    tooltip={t('users.form.licenseTypeHelp')}
                  />
                }
                required
                fullWidth
                size="small"
              >
                <MenuItem value={LicenseType.LITE}>{t('users.form.licenseTypes.lite')}</MenuItem>
                <MenuItem value={LicenseType.POWER}>{t('users.form.licenseTypes.power')}</MenuItem>
                {hasAdminEnablement && (
                  <MenuItem value={LicenseType.ADMIN}>
                    {t('users.form.licenseTypes.admin')}
                  </MenuItem>
                )}
              </HookFormSelect>
            </Grid>

            {showIntegrationsRole && (
              <Grid size={{ xs: 6 }}>
                <HookFormSelect<EditUserFormValues>
                  name="integrationsRole"
                  label={
                    <InfoLabel
                      label={t('users.form.integrationsRole')}
                      tooltip={t('users.form.integrationsRoleHelp')}
                    />
                  }
                  fullWidth
                  size="small"
                  disabled={hasExistingIntegrationRole}
                >
                  <MenuItem value={IntegrationRoleOption.NOT_SELECTED}>
                    {t('users.form.integrationsRoles.notSelected')}
                  </MenuItem>
                  <MenuItem value={IntegrationRoleOption.ANALYST}>
                    {t('users.form.integrationsRoles.analyst')}
                  </MenuItem>
                  <MenuItem value={IntegrationRoleOption.OPERATOR}>
                    {t('users.form.integrationsRoles.operator')}
                  </MenuItem>
                  <MenuItem value={IntegrationRoleOption.ADMIN}>
                    {t('users.form.integrationsRoles.admin')}
                  </MenuItem>
                </HookFormSelect>
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <Stack>
                <HookFormCheckbox<EditUserFormValues>
                  name="projectParticipant"
                  label={t('users.form.projectParticipant')}
                  helperText={t('users.form.projectParticipantHelp')}
                />
                <HookFormCheckbox<EditUserFormValues>
                  name="emailNotifications"
                  label={t('users.form.emailNotifications')}
                  helperText={t('users.form.emailNotificationsHelp')}
                />
              </Stack>

              <Collapse in={emailNotifications}>
                <Box sx={{ pl: 4, pt: 2, width: '50%' }}>
                  <HookFormSelect<EditUserFormValues>
                    name="notificationFrequency"
                    label={t('users.form.notificationFrequency')}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value={NotificationFrequencyOption.IMMEDIATELY}>
                      {t('users.form.frequencies.immediately')}
                    </MenuItem>
                    <MenuItem value={NotificationFrequencyOption.DAILY}>
                      {t('users.form.frequencies.daily')}
                    </MenuItem>
                    <MenuItem value={NotificationFrequencyOption.WEEKLY}>
                      {t('users.form.frequencies.weekly')}
                    </MenuItem>
                    <MenuItem value={NotificationFrequencyOption.MONTHLY}>
                      {t('users.form.frequencies.monthly')}
                    </MenuItem>
                  </HookFormSelect>
                </Box>
              </Collapse>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </SideDrawer>
  )
}
